import React, { ReactNode, useContext, useState } from "react";
import { Control, Controller, DeepMap, FieldError, RegisterOptions } from "react-hook-form";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Label as AriaLabel,
  OverlayArrow,
  Popover,
  DateValue,
  DatePickerStateContext,
} from "react-aria-components";
import { Label } from "semantic-ui-react";
import { parseAbsolute, toZoned } from "@internationalized/date";
import { useTimezone } from "../../../../hooks/useTimezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/pro-regular-svg-icons";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { Segment, Button } from "semantic-ui-react";
import { ZonedDateTime, ZonedDateTimeProps } from "../../ZonedDateTime";
import { fieldError } from "../../../../utils/functions";
import classNames from "classnames";

type SharedDateTimeProps = {
  granularity?: "day" | "hour" | "minute" | "second";
  timeWithRespectToAgencyTimezone?: boolean;
  readonly?: boolean;
  isDateUnavailable?: (date: DateValue) => boolean;
  isInvalid?: boolean;
  UNSTABLE_portalContainer?: Element | undefined;
  label?: ReactNode;
  required?: boolean;
  className?: string;
  clearable?: boolean;
  zonedDateTimeProps?: Partial<ZonedDateTimeProps>;
  onBlur?: () => void;
};

type ControlledDateTimeProps<FormValue, Key extends string> = SharedDateTimeProps & {
  control: Control;
  name: string;
  defaultValue?: Date | string;
  errors?: DeepMap<Record<Key, FormValue>, FieldError>;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export function ControlledDateTime<FormValue, Key extends string>(props: ControlledDateTimeProps<FormValue, Key>) {
  const {
    control,
    name,
    label,
    granularity = "minute",
    timeWithRespectToAgencyTimezone = true,
    readonly = false,
    errors,
    required = false,
    rules,
    className,
    clearable = false,
    defaultValue,
    onBlur,
    zonedDateTimeProps,
  } = props;

  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules ?? {}}
      render={({ onChange, value }) => {
        return (
          <DateTime
            granularity={granularity}
            timeWithRespectToAgencyTimezone={timeWithRespectToAgencyTimezone}
            readonly={readonly}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            label={label}
            required={required}
            error={error?.content.message}
            UNSTABLE_portalContainer={props.UNSTABLE_portalContainer}
            isDateUnavailable={props.isDateUnavailable}
            isInvalid={props.isInvalid}
            className={className}
            clearable={clearable}
            zonedDateTimeProps={zonedDateTimeProps}
          />
        );
      }}
    />
  );
}

type DateTimeProps = SharedDateTimeProps & {
  readonly?: boolean;
  error?: string;
  value?: Date;
  onChange?: ((value?: Date) => void) | undefined;
};

export const DateTime = ({
  granularity = "minute",
  timeWithRespectToAgencyTimezone = true,
  readonly = false,
  isDateUnavailable,
  UNSTABLE_portalContainer,
  error,
  value,
  onChange,
  onBlur,
  label,
  required = false,
  isInvalid = false,
  className,
  clearable = false,
  zonedDateTimeProps,
}: DateTimeProps) => {
  const [focused, setFocused] = useState(false);
  const state = useContext(DatePickerStateContext)!;
  const timezoneInfo = useTimezone({ defaultToAgencyTimezone: timeWithRespectToAgencyTimezone });

  const timezone = timezoneInfo.defaultTimezoneInfo.timezone;

  const shouldHideTimezone = !timeWithRespectToAgencyTimezone || timezoneInfo.clientIsInAgencyTimezone;
  const zonedDate = value ? parseAbsolute(new Date(value).toISOString(), timezone) : null;

  const DatePickerClearButton = () => {
    return (
      <AriaButton
        // Don't inherit default Button behavior from DatePicker.
        slot={null}
        className='clear-button tw-mr-1 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-font-bold tw-leading-[1] tw-text-[#000]'
        aria-label='Clear'
        onPress={() => {
          state.setValue(null);
          onChange?.(undefined);
        }}
      >
        ✕
      </AriaButton>
    );
  };

  if (readonly) {
    return (
      <div className='tw-flex tw-flex-col'>
        {label && <label className='tw-font-bold'>{label}</label>}
        <ZonedDateTime value={zonedDate?.toDate()} {...zonedDateTimeProps} />
      </div>
    );
  }

  return (
    <DatePicker
      className={classNames(className, "tw-flex tw-flex-col")}
      value={zonedDate}
      onChange={(value) => {
        if (value && onChange) {
          /**
           * Making these changes for EN-7359. If we break a bunch of stuff, we can take it out or add
           * a toggle prop to the DatePicker to enable this behavior. On the other hand, if its been
           * awhile since we've committed these changes and nobody has complained about the feature, we
           * can probably remove this comment -- Peter
           */
          const updatedValue = value.set({ year: snapYear(value.year) });
          onChange(toZoned(updatedValue, timezone).toDate());
        }
      }}
      onBlur={() => {
        if (onBlur) onBlur();
      }}
      onFocusChange={(isFocused) => {
        setFocused(isFocused);
      }}
      hideTimeZone={shouldHideTimezone}
      granularity={granularity}
      validate={() => error || null}
      isInvalid={isInvalid || Boolean(error)}
    >
      {label && (
        <AriaLabel className='label-text tw-mb-1'>
          {label}
          {required && <span className='tw-ml-1 tw-text-red'>*</span>}
        </AriaLabel>
      )}
      <Group
        className={classNames({
          "accessible-input tw-flex tw-justify-between tw-gap-1 tw-rounded-[.285rem] tw-border-[1px] tw-border-solid tw-px-4 tw-py-[0.53rem] tw-transition-shadow tw-duration-75": true,
          "tw-border-[#22242626]": !focused,
          "tw-border-[#7faecfbf]": focused,
          "tw-border-[#E0B4B4] tw-bg-[#FFF6F6]": !!error,
        })}
      >
        <DateInput className='tw-flex tw-h-6 tw-items-center'>
          {(segment) => {
            if (segment.type === "literal" && segment.text === ", ")
              return <div className='tw-pr-1'>{segment.text}</div>;
            if (segment.type === "timeZoneName") return <div className='tw-pl-1'>{segment.text}</div>;
            return (
              <DateSegment
                segment={segment}
                className='accessible-input focus:tw-rounded-md focus:tw-ring-2 focus:tw-ring-[#7faecfbf] focus-visible:tw-outline-none'
                aria-invalid={Boolean(error)}
              />
            );
          }}
        </DateInput>
        <div className='tw-flex'>
          {clearable && value && <DatePickerClearButton />}
          <AriaButton className='accessible-input tw-mr-0 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-bg-transparent tw-p-0 tw-text-grey hover:tw-text-black focus:tw-ring-2 focus:tw-ring-[#7faecfbf] focus:tw-ring-offset-2 focus-visible:tw-outline-none'>
            <FontAwesomeIcon className='tw-text-xl tw-font-bold' icon={faCalendarAlt} />
          </AriaButton>
        </div>
      </Group>
      {error && (
        <Label basic prompt pointing='above' aria-live='polite'>
          {error}
        </Label>
      )}
      <Popover UNSTABLE_portalContainer={UNSTABLE_portalContainer} placement='bottom end'>
        <OverlayArrow></OverlayArrow>
        <Segment className='tw-m-0 tw-shadow-md'>
          <Dialog>
            <Calendar isDateUnavailable={isDateUnavailable} className={"tw-flex tw-flex-col tw-items-center"}>
              <div className={"tw-mb-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-1"}>
                <AriaButton slot='previous' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
                  <Button
                    as='div'
                    className='button-ghost tw-mr-0 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center'
                    icon
                    size='tiny'
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-grey' icon={faAngleLeft} />
                  </Button>
                </AriaButton>
                <Heading className='tw-m-0 tw-text-xl' />
                <AriaButton slot='next' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
                  <Button
                    as='div'
                    className='button-ghost tw-mr-0 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center'
                    icon
                    size='tiny'
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-grey' icon={faAngleRight} />
                  </Button>
                </AriaButton>
              </div>
              <CalendarGrid>
                <CalendarGridHeader className='tw-h-6'>
                  {(day) => (
                    <CalendarHeaderCell className='tw-text-center tw-text-xs tw-text-grey'>{day}</CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => {
                    return (
                      <CalendarCell
                        className={({ isUnavailable, isDisabled, isSelected, isOutsideMonth }) => {
                          const base =
                            "tw-flex tw-h-8 tw-w-8 tw-cursor-default tw-items-center tw-justify-center tw-rounded-md tw-text-sm tw-forced-color-adjust-none";
                          if (isDisabled || isUnavailable) return `${base} tw-text-grey`;
                          if (isSelected) return `${base} tw-bg-blue tw-text-white`;
                          if (!isOutsideMonth) return `${base} hover:tw-cursor-pointer hover:tw-bg-grey-light`;
                          return `${base} tw-text-black`;
                        }}
                        date={date}
                      />
                    );
                  }}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Segment>
      </Popover>
    </DatePicker>
  );
};

function snapYear(year: number): number {
  if (!shouldSnapYear(year)) {
    return year;
  }
  if (year < 50) {
    return year + 2000;
  }
  return year + 1900;
}

function shouldSnapYear(year: number): boolean {
  return year < 100;
}
